import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Grid } from '@mui/material';
import { HSpacer, RoutalLoader as Loader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import PinField from 'react-pin-field';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { PIN } from '../../../../../../../../../assets/svg/PIN';
import { RefreshIcon } from '../../../../../../../../../assets/svg/RefreshIcon';
import { FullPageContainer } from '../../../../../../../../../components/FullPageContainer';
import { LoadingButton } from '../../../../../../../../../components/LoadingButton';
import { Stop } from '../../../../../../../../../domain/stop/Stop';
import Button from '../../../../../../../../../react-components/Button';
import { Text } from '../../../../../../../../../react-components/Text';
import { useSendPin } from './useSendPin';
import { useValidatePin } from './useValidatePîn';

const maskEmail = (email: string) => {
  const [username, domain] = email.split(`@`);
  const maskedUsername = `${username.slice(0, 3)}*****${username.slice(-3)}`;
  return `${maskedUsername}@${domain}`;
};

const maskPhone = (phone: string) => {
  return `${phone.slice(0, 3)}*****${phone.slice(-3)}`;
};

const ResendPinButton = ({ stop }: { stop: Stop }) => {
  const [resendMethod, setResendMethod] = useState<`email` | `sms` | undefined>(undefined);
  const [selectResendOptionVisible, setSelectResendOptionVisible] = useState<boolean>(false);
  const {
    isLoading: sendPinIsLoading,
    success: sendPinSuccess,
    error: sendPinError,
    run: sendPin,
  } = useSendPin({ stop, method: resendMethod });

  useEffect(() => {
    if (resendMethod && !sendPinIsLoading) {
      sendPin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendMethod]);

  const hasEmailField = stop.stop_fields.find((field) => field.type === `email`);
  const hasPhoneField = stop.stop_fields.find((field) => field.type === `phone`);

  if (!hasEmailField?.value || !hasPhoneField?.value) {
    //AND???
    return null;
  }

  return (
    <Grid container direction="column">
      {!selectResendOptionVisible ? (
        <>
          {!sendPinIsLoading && !sendPinSuccess && !sendPinError ? (
            <Grid item container direction="row" justifyContent="center">
              <Button
                variant="text"
                style={{
                  border: `none`,
                  display: `flex`,
                  cursor: `pointer`,
                }}
                onClick={() => {
                  if (hasEmailField && hasPhoneField?.value) {
                    setSelectResendOptionVisible(true);
                    return;
                  }

                  if (hasEmailField) {
                    setResendMethod(`sms`);
                    return;
                  }

                  if (hasPhoneField?.value) {
                    setResendMethod(`email`);
                    return;
                  }
                }}
              >
                <RefreshIcon />
                <HSpacer small />
                <Text
                  variant="button"
                  intlId="driver.report_complete_pin.resend_pin"
                  style={{
                    cursor: `pointer`,
                    color: `inherit`,
                  }}
                />
              </Button>
            </Grid>
          ) : (
            <>
              <VSpacer medium />
              <Grid container direction="row" alignItems="center">
                <span>
                  <CheckCircleOutlineOutlinedIcon
                    style={{ color: RoutalPalette.success.medium, fontSize: `16px`, marginRight: `8px` }}
                  />
                  <Text
                    tagName="span"
                    variant="label"
                    style={{ fontSize: `13px` }}
                    intlId="driver.report_complete_pin.resend_pin_done"
                    intlValues={{
                      value: hasEmailField?.value ? maskPhone(hasPhoneField?.value) : maskEmail(hasEmailField?.value),
                    }}
                  />
                </span>
              </Grid>
            </>
          )}
        </>
      ) : (
        <Grid container direction="column">
          <Grid
            item
            container
            direction="row"
            alignItems="center"
            onClick={() => {
              setResendMethod(`email`);
              setSelectResendOptionVisible(false);
            }}
            style={{
              cursor: `pointer`,
              padding: `6px 10px`,
              border: `1px solid ${RoutalPalette.primary40}`,
              borderRadius: `2px`,
              height: `45px`,
            }}
          >
            <Grid item>
              <MailOutlineIcon style={{ cursor: `pointer`, color: RoutalPalette.primary40 }} />
            </Grid>

            <HSpacer small />

            <Grid item>
              <Text
                variant="label"
                style={{ cursor: `pointer`, color: RoutalPalette.primary40 }}
                intlId="driver.report_complete_pin.resend_pin_to"
                intlValues={{ value: maskEmail(hasEmailField?.value) }}
              />
            </Grid>
          </Grid>

          <VSpacer small />

          <Grid
            item
            container
            direction="row"
            alignItems="center"
            onClick={() => {
              setResendMethod(`sms`);
              setSelectResendOptionVisible(false);
            }}
            style={{
              cursor: `pointer`,
              padding: `6px 10px`,
              border: `1px solid ${RoutalPalette.primary40}`,
              borderRadius: `2px`,
              height: `45px`,
            }}
          >
            <Grid item>
              <LocalPhoneOutlinedIcon style={{ cursor: `pointer`, color: RoutalPalette.primary40 }} />
            </Grid>

            <HSpacer small />

            <Grid item>
              <Text
                variant="label"
                style={{ cursor: `pointer`, color: RoutalPalette.primary40 }}
                intlId="driver.report_complete_pin.resend_pin_to"
                intlValues={{ value: maskPhone(hasPhoneField?.value) }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const StyledReportCompletePin = styled(CSSTransition)`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1005;
  height: 100%;
  width: 100%;

  &.report-complete-pin-enter {
    opacity: 0;
  }
  &.report-complete-pin-enter-active {
    opacity: 1;
    transition: opacity 200ms;
  }
  &.report-complete-pin-exit {
    opacity: 1;
  }
  &.report-complete-pin-exit-active {
    opacity: 0;
    transition: opacity 200ms;
  }
`;

const StyledPinField = styled(PinField)`
  flex-direction: row;
  align-content: space-around;
  justify-content: center;
  flex-wrap: nowrap;
  width: 50px;
  height: 75px;
  margin-right: 8px;
  margin-left: 8px;
  font-size: 22px;
  text-align: center;
  font-weight: 600;
  font-color: ${RoutalPalette.secondary40};
  color: ${RoutalPalette.secondary40};
  border: 1px solid ${RoutalPalette.primary40};
  user-select: none;
  border-radius: 10px;

  :focus {
    outline: none;
    box-shadow: inset ${RoutalPalette.primary40} 0px 0px 0px 2px !important;
    border-radius: 10px;
  }

  @media (max-width: 320px) {
    margin-right: 4px;
    margin-left: 4px;
  }
  @media (max-width: 270px) {
    margin-right: 2px;
    margin-left: 2px;
    width: 40px;
  }
  :nth-of-type(1) {
    margin-left: 0px;
  }
  :nth-last-of-type(1) {
    margin-right: 0px;
  }
`;

interface ReportCompletePinProps {
  open: boolean;
  stop: Stop;
  onCancel?: () => void;
  onConfirm?: (pin: string) => void;
  isLoading?: boolean;
}

export const ReportCompletePin = ({ open = false, stop, onCancel, onConfirm, isLoading }: ReportCompletePinProps) => {
  const [pin, setPin] = useState<string>(``);
  const {
    isLoading: pinIsLoading,
    success: pinSuccess,
    error: codeError,
    run,
  } = useValidatePin({
    stop,
    pin: pin,
  });

  useEffect(() => {
    if (pin.length === 3) {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  useEffect(() => {
    if (pin !== `` && pinSuccess && onConfirm) {
      onConfirm(pin);
      // setPin(``);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinSuccess]);

  useEffect(() => {
    if (codeError) {
      setPin(``);
    }
  }, [codeError]);

  return (
    <StyledReportCompletePin in={open} timeout={200} classNames="report-complete-pin" unmountOnExit>
      <FullPageContainer
        style={{
          backgroundColor: RoutalPalette.white,
          padding: `40px`,
        }}
      >
        <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
          <VSpacer large />
          <VSpacer large />

          <Grid container direction="column" alignItems="center" justifyContent="center">
            <PIN style={{ width: `34px`, height: `36px` }} />
            <VSpacer small />
            <Text
              variant="h5"
              intlId="driver.report_complete_pin.title"
              style={{ color: RoutalPalette.primary40, textAlign: `center`, fontWeight: `normal` }}
            />
          </Grid>

          <VSpacer large />
          <VSpacer medium />

          <Grid container direction="column" justifyContent="flex-start" style={{ flex: 1, overflowY: `auto` }}>
            {codeError && pin === `` ? (
              <>
                <Grid container direction="row" alignItems="center" justifyContent="center">
                  <Grid item>
                    <ErrorOutlineOutlinedIcon style={{ color: RoutalPalette.error.medium }} />
                  </Grid>

                  <HSpacer small />

                  <Grid item>
                    <Text style={{ color: RoutalPalette.error.medium }} intlId="driver.report_complete_pin.error" />
                  </Grid>
                </Grid>
                <VSpacer medium />
              </>
            ) : null}

            {pinIsLoading ? (
              <Grid container direction="row" alignItems="center" justifyContent="center">
                <Loader width={42} />
              </Grid>
            ) : (
              <Grid item style={{ display: `flex`, justifyContent: `center` }}>
                <StyledPinField
                  className="pin-field"
                  inputMode="text"
                  onChange={(value: string) => {
                    setPin((value ?? ``).toUpperCase());
                  }}
                  format={(char: string) => char.toUpperCase()}
                  autoComplete="one-time-code"
                  length={3}
                  autoFocus
                />
              </Grid>
            )}

            <VSpacer medium />

            <Grid container direction="row" alignItems="center" justifyContent="center">
              <ResendPinButton stop={stop} />
            </Grid>
          </Grid>

          <VSpacer medium />

          <Grid container direction="column">
            <LoadingButton
              isLoading={pinIsLoading || isLoading}
              variant="default"
              onClick={() => {
                run();
              }}
              style={{
                height: `40px`,
                fontSize: `18px`,
                fontWeight: `normal`,
                borderRadius: `10px`,
              }}
              intlId="send"
            />

            <VSpacer small />

            <LoadingButton
              disabled={pinIsLoading || isLoading}
              onClick={() => {
                if (onCancel) {
                  onCancel();
                  setPin(``);
                }
              }}
              variant="flat"
              style={{
                height: `40px`,
                fontSize: `18px`,
                fontWeight: `normal`,
                borderRadius: `10px`,
              }}
              intlId="cancel"
            />
          </Grid>
        </Grid>
      </FullPageContainer>
    </StyledReportCompletePin>
  );
};
